import type React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useNavigate} from "react-router-dom";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockIcon from "@mui/icons-material/Lock";
import RemoveIcon from "@mui/icons-material/Remove";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {createTenant} from "../store/tenantsSlice";
import {fetchPlans, plansSelectors, type Plan} from "../store/plansSlice";
import {addQuotaChange} from "../api/tenantDetailApi";
import {getGlobalFeatureFlags} from "../api/configurationApi";
import {defaultColor} from "../types/tenant-create";
import type {GlobalFeatureFlags} from "../types/tenant";
import {defaultAvailabilityRules, uniformWorkingHours} from "../util/default-availability";
import {
    buildCreateTenantRequest,
    type CreateTenantField,
    type CreateTenantForm,
    initialCreateTenantForm,
    initialQuotaChanges,
    TERM_OPTIONS_MONTHS,
    termEndDates,
    validateCreateTenantForm,
} from "../utils/createTenantForm";
import {summarizeOverrides} from "../utils/featureFlagResolution";
import {TENANT_FORM_FEATURE_FLAGS} from "../constants/featureFlags";
import {PageHeader} from "../components/ui/PageHeader";
import {UnsavedChangesGuard} from "../components/ui/UnsavedChangesGuard";
import {quotaTypeLabelKey} from "../components/tenant-detail/QuotaDialogs";
import type {TenantDetailNavigationState} from "./TenantDetailPage";
import {SectionCard} from "../components/ui/SectionCard";
import {KeyHint} from "../components/ui/StatusChip";
import {SmsProviderSelect, TenantFeatureFlagsEditor} from "../components/feature-flags/TenantFeatureFlagsEditor";
import {colors} from "../theme/tokens";
import {formatCount, formatDate, formatEuroCents, formatStorageBytes} from "../util/format";

const BYTES_PER_MEGABYTE = 1024 * 1024;

export function CreateTenantPage() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const plans = useAppSelector(plansSelectors.selectAll);
    const plansStatus = useAppSelector((s) => s.plans.status);
    const createStatus = useAppSelector((s) => s.tenants.createStatus);

    const [form, setForm] = useState<CreateTenantForm>(() => initialCreateTenantForm(new Date()));
    const [touched, setTouched] = useState<Partial<Record<CreateTenantField, boolean>>>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [globalFlags, setGlobalFlags] = useState<GlobalFeatureFlags | null>(null);
    // Set once the tenant exists, so leaving for its page skips the unsaved-changes question.
    const [leaveTo, setLeaveTo] = useState<{path: string; state?: TenantDetailNavigationState} | null>(null);
    const [availabilityRules] = useState(() => defaultAvailabilityRules(new Date()));

    useEffect(() => {
        if (plansStatus === "idle") void dispatch(fetchPlans());
    }, [dispatch, plansStatus]);

    useEffect(() => {
        let active = true;
        getGlobalFeatureFlags()
                .then((flags) => {
                    if (active) setGlobalFlags(flags);
                })
                .catch(() => undefined);
        return () => {
            active = false;
        };
    }, []);

    const errors = useMemo(() => validateCreateTenantForm(form), [form]);
    const errorCount = Object.keys(errors).length;
    const fieldError = (field: CreateTenantField): string | undefined =>
            (submitAttempted || touched[field]) && errors[field] ? t(errors[field]) : undefined;
    const touch = (field: CreateTenantField) => setTouched((current) => ({...current, [field]: true}));
    const dirty = Boolean(form.name.trim() || form.administratorEmail.trim() || form.notes.trim());
    const selectedPlan = plans.find((plan) => plan.id === form.planId) ?? null;

    const update = (changes: Partial<CreateTenantForm>) => setForm((current) => ({...current, ...changes}));

    const chooseTerm = (months: number) => update({termMonths: months, ...termEndDates(form.startDate, months)});
    const changeStartDate = (startDate: string) =>
            update(form.termMonths && startDate ? {startDate, ...termEndDates(startDate, form.termMonths)} : {startDate});
    const choosePlan = (plan: Plan | null) => {
        // Plans without a base price are free; a priced plan switches the amounts back on.
        update({planId: plan?.id ?? "", ...(plan && {isFreePlan: plan.monthlyBasePriceInCents <= 0})});
        touch("planId");
    };

    const handleSubmit = useCallback(async () => {
        setSubmitAttempted(true);
        setSubmitError(null);
        if (Object.keys(validateCreateTenantForm(form)).length > 0) return;
        const request = buildCreateTenantRequest(form, {color: defaultColor(), availabilityRules});
        const result = await dispatch(createTenant(request));
        if (!createTenant.fulfilled.match(result)) {
            setSubmitError(result.error.message ?? t("CREATE_FAILED"));
            return;
        }
        const created = result.payload;
        // Every grant is attempted; the ones that fail are named on the new tenant's page, where Edit quotas repairs them.
        const failed: string[] = [];
        for (const change of initialQuotaChanges(form.quotas)) {
            try {
                await addQuotaChange(created.id, change);
            } catch (e) {
                failed.push(`${t(quotaTypeLabelKey(change.quota))} (${e instanceof Error ? e.message : t("DETAIL_FAILED")})`);
            }
        }
        setLeaveTo({
            path: `/tenants/${created.id}`,
            state: failed.length > 0 ? {notice: t("CREATE_QUOTAS_NOT_APPLIED", {quotas: failed.join(", ")})} : undefined,
        });
    }, [form, availabilityRules, dispatch, t]);

    useEffect(() => {
        if (leaveTo) navigate(leaveTo.path, {state: leaveTo.state});
    }, [leaveTo, navigate]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (document.querySelector(".MuiDialog-root, .MuiAutocomplete-popper")) return;
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault();
                if (createStatus !== "loading") void handleSubmit();
            }
            if (event.key === "Escape") navigate("/tenants");
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [createStatus, handleSubmit, navigate]);

    const flagSummary = summarizeOverrides(form.featureConfig, TENANT_FORM_FEATURE_FLAGS);
    const workingHours = uniformWorkingHours(availabilityRules);

    return (
            <>
                <PageHeader
                        title={t("CREATE_TITLE")}
                        breadcrumbs={[
                            {label: t("NAV_TENANTS"), to: "/tenants", ariaLabel: t("DETAIL_BREADCRUMB_TENANTS_ARIA")},
                            {label: t("CREATE_BREADCRUMB_NEW")},
                        ]}
                />

                {submitError && <Alert severity="error" onClose={() => setSubmitError(null)}>{submitError}</Alert>}

                <Box
                        component="form"
                        noValidate
                        onSubmit={(event: React.FormEvent) => {
                            event.preventDefault();
                            void handleSubmit();
                        }}
                        sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 380px"}, gap: "14px", alignItems: "start"}}
                >
                    <Box sx={{display: "flex", flexDirection: "column", gap: "14px", minWidth: 0}}>
                        <SectionCard number={1} title={t("CREATE_SECTION_TENANT")}>
                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr 180px"}, gap: 2}}>
                                <TextField
                                        label={t("CREATE_LABEL_NAME")}
                                        required
                                        size="small"
                                        value={form.name}
                                        onChange={(e) => update({name: e.target.value})}
                                        onBlur={() => touch("name")}
                                        error={Boolean(fieldError("name"))}
                                        helperText={fieldError("name")}
                                        slotProps={{htmlInput: {maxLength: 256}}}
                                />
                                <TextField
                                        label={t("CREATE_LABEL_ADMINISTRATOR_EMAIL")}
                                        required
                                        type="email"
                                        size="small"
                                        value={form.administratorEmail}
                                        onChange={(e) => update({administratorEmail: e.target.value})}
                                        onBlur={() => touch("administratorEmail")}
                                        error={Boolean(fieldError("administratorEmail"))}
                                        helperText={fieldError("administratorEmail")}
                                />
                                <TextField
                                        label={t("CREATE_LABEL_COUNTRY")}
                                        size="small"
                                        value={t("CREATE_COUNTRY_AUSTRIA")}
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                                endAdornment: (
                                                        <InputAdornment position="end">
                                                            <LockIcon sx={{fontSize: 16, color: colors.faint}} />
                                                        </InputAdornment>
                                                ),
                                            },
                                        }}
                                />
                                <TextField
                                        label={t("DETAIL_NOTES")}
                                        size="small"
                                        multiline
                                        minRows={2}
                                        value={form.notes}
                                        onChange={(e) => update({notes: e.target.value})}
                                        onBlur={() => touch("notes")}
                                        error={Boolean(fieldError("notes"))}
                                        helperText={fieldError("notes")}
                                        slotProps={{htmlInput: {maxLength: 50000}}}
                                        sx={{gridColumn: "1 / -1"}}
                                />
                            </Box>
                        </SectionCard>

                        <SectionCard number={2} title={t("CREATE_SECTION_SUBSCRIPTION")}>
                            <Autocomplete
                                    options={plans}
                                    value={selectedPlan}
                                    onChange={(_event, plan) => choosePlan(plan)}
                                    getOptionLabel={(plan) => plan.name}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    loading={plansStatus === "loading"}
                                    sx={{maxWidth: 480}}
                                    renderOption={(props, plan, {selected}) => {
                                        const {key, ...optionProps} = props;
                                        return (
                                                <Box component="li" key={key} {...optionProps} sx={{minHeight: 34, gap: 1}}>
                                                    <Box component="span" sx={{width: 18, display: "inline-flex"}}>
                                                        {selected && <CheckIcon sx={{fontSize: 16, color: colors.primary}} />}
                                                    </Box>
                                                    <Box component="span" sx={{flex: 1, fontWeight: 500}}>{plan.name}</Box>
                                                    <Box component="span" sx={{color: colors.muted, fontSize: "12px"}}>
                                                        {t("CREATE_PLAN_PRICE_PER_MONTH", {price: formatEuroCents(plan.monthlyBasePriceInCents)})}
                                                    </Box>
                                                </Box>
                                        );
                                    }}
                                    renderInput={(params) => (
                                            <TextField
                                                    {...params}
                                                    label={t("CREATE_LABEL_PLAN")}
                                                    required
                                                    size="small"
                                                    placeholder={t("DETAIL_SELECT_PLAN")}
                                                    onBlur={() => touch("planId")}
                                                    error={Boolean(fieldError("planId"))}
                                                    helperText={fieldError("planId") ?? t("CREATE_PLAN_COUNT", {count: plans.length})}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                                <>
                                                                    {selectedPlan && (
                                                                            <Box component="span" sx={{color: colors.muted, fontSize: "12px", mr: 1, whiteSpace: "nowrap"}}>
                                                                                {t("CREATE_PLAN_PRICE_PER_MONTH", {price: formatEuroCents(selectedPlan.monthlyBasePriceInCents)})}
                                                                            </Box>
                                                                    )}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                        ),
                                                    }}
                                            />
                                    )}
                            />

                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 2, mt: 2, alignItems: "flex-start"}}>
                                <EmployeeStepper
                                        value={form.maxEmployees}
                                        onChange={(maxEmployees) => update({maxEmployees})}
                                        error={fieldError("maxEmployees")}
                                />
                                <Box>
                                    <Typography component="div" sx={{fontSize: "12px", color: colors.muted, mb: 0.5}}>
                                        {t("CREATE_TERM_LABEL")}
                                    </Typography>
                                    <Box role="group" aria-label={t("CREATE_TERM_LABEL")} sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                                        {TERM_OPTIONS_MONTHS.map((months) => {
                                            const selected = form.termMonths === months;
                                            return (
                                                    <ButtonBase
                                                            key={months}
                                                            onClick={() => chooseTerm(months)}
                                                            aria-pressed={selected}
                                                            sx={{
                                                                height: 34,
                                                                px: 1.75,
                                                                borderRadius: "4px",
                                                                fontSize: "13px",
                                                                border: `1px solid ${selected ? colors.primary : colors.inputBorder}`,
                                                                backgroundColor: selected ? colors.infoTint : "#fff",
                                                                color: selected ? colors.chrome : colors.body,
                                                                fontWeight: selected ? 500 : 400,
                                                            }}
                                                    >
                                                        {t("CREATE_TERM_MONTHS", {count: months})}
                                                    </ButtonBase>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"}, gap: 2, mt: 2}}>
                                <TextField
                                        label={t("DETAIL_START_DATE")}
                                        type="date"
                                        size="small"
                                        value={form.startDate}
                                        onChange={(e) => changeStartDate(e.target.value)}
                                />
                                <TextField
                                        label={t("DETAIL_PAID_UNTIL")}
                                        type="date"
                                        required
                                        size="small"
                                        value={form.paidUntil}
                                        onChange={(e) => update({paidUntil: e.target.value, termMonths: null})}
                                        onBlur={() => touch("paidUntil")}
                                        error={Boolean(fieldError("paidUntil"))}
                                        helperText={fieldError("paidUntil") ?? (form.termMonths ? t("CREATE_PAID_UNTIL_HINT") : undefined)}
                                />
                                <TextField
                                        label={t("DETAIL_ACTIVE_UNTIL")}
                                        type="date"
                                        required
                                        size="small"
                                        value={form.activeUntil}
                                        onChange={(e) => update({activeUntil: e.target.value, termMonths: null})}
                                        onBlur={() => touch("activeUntil")}
                                        error={Boolean(fieldError("activeUntil"))}
                                        helperText={fieldError("activeUntil") ?? (form.termMonths ? t("CREATE_ACTIVE_UNTIL_HINT") : undefined)}
                                />
                            </Box>

                            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${colors.sectionDivider}`}}>
                                <FormControlLabel
                                        control={<Switch checked={form.isFreePlan} onChange={(e) => update({isFreePlan: e.target.checked})} />}
                                        label={t("DETAIL_FREE_PLAN")}
                                        sx={{ml: 0, mt: 2.5}}
                                />
                                <Box sx={{flex: 1}} />
                                {!form.isFreePlan && (
                                        <>
                                            <TextField
                                                    label={t("DETAIL_AMOUNT_DUE")}
                                                    type="number"
                                                    size="small"
                                                    slotProps={{htmlInput: {min: 0, step: 0.01}}}
                                                    value={form.amountDue}
                                                    onChange={(e) => update({amountDue: Number(e.target.value)})}
                                                    error={Boolean(fieldError("amountDue"))}
                                                    helperText={fieldError("amountDue")}
                                                    sx={{width: 150}}
                                            />
                                            <TextField
                                                    label={t("DETAIL_AMOUNT_PAID")}
                                                    type="number"
                                                    size="small"
                                                    slotProps={{htmlInput: {min: 0, step: 0.01}}}
                                                    value={form.amountPaid}
                                                    onChange={(e) => update({amountPaid: Number(e.target.value)})}
                                                    error={Boolean(fieldError("amountPaid"))}
                                                    helperText={fieldError("amountPaid")}
                                                    sx={{width: 150}}
                                            />
                                        </>
                                )}
                            </Box>
                        </SectionCard>

                        <SectionCard
                                number={3}
                                title={t("DETAIL_FEATURES")}
                                helper={t("CREATE_FEATURES_HELPER")}
                                actions={<SmsProviderSelect id="create-sms-provider" value={form.featureConfig} onChange={(featureConfig) => update({featureConfig})} />}
                        >
                            <TenantFeatureFlagsEditor
                                    value={form.featureConfig}
                                    onChange={(featureConfig) => update({featureConfig})}
                                    globalFlags={globalFlags}
                                    lockedFlags={{firstLaunch: {value: true, hint: t("CREATE_FIRST_LAUNCH_LOCKED")}}}
                            />
                        </SectionCard>

                        <SectionCard number={4} title={t("CREATE_SECTION_INITIAL_QUOTAS")}>
                            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2}}>
                                <TextField
                                        label={t("DETAIL_QUOTA_SMS")}
                                        type="number"
                                        size="small"
                                        slotProps={{htmlInput: {min: 0}}}
                                        value={form.quotas.sms}
                                        onChange={(e) => update({quotas: {...form.quotas, sms: Number(e.target.value)}})}
                                        sx={{width: 150}}
                                />
                                <TextField
                                        label={t("CREATE_LABEL_STORAGE_MB")}
                                        type="number"
                                        size="small"
                                        slotProps={{htmlInput: {min: 0}}}
                                        value={form.quotas.storageMegabytes}
                                        onChange={(e) => update({quotas: {...form.quotas, storageMegabytes: Number(e.target.value)}})}
                                        sx={{width: 150}}
                                />
                                <TextField
                                        label={t("DETAIL_QUOTA_EMAIL")}
                                        type="number"
                                        size="small"
                                        slotProps={{htmlInput: {min: 0}}}
                                        value={form.quotas.email}
                                        onChange={(e) => update({quotas: {...form.quotas, email: Number(e.target.value)}})}
                                        sx={{width: 150}}
                                />
                                <Typography variant="body2" sx={{color: colors.muted, pb: 1}}>{t("CREATE_QUOTAS_USAGE_STARTS_AT_ZERO")}</Typography>
                            </Box>
                        </SectionCard>
                    </Box>

                    <Box sx={{display: "flex", flexDirection: "column", gap: "14px", position: {lg: "sticky"}, top: 72}}>
                        <Paper variant="outlined" component="section" aria-label={t("CREATE_SUMMARY_TITLE")}>
                            <Box sx={{px: 2, py: 1.5, borderBottom: `1px solid ${colors.sectionDivider}`}}>
                                <Typography variant="h6" component="h2">{t("CREATE_SUMMARY_TITLE")}</Typography>
                            </Box>
                            <Box sx={{px: 2, py: 1.5, borderBottom: `1px solid ${colors.sectionDivider}`}}>
                                <Typography sx={{fontWeight: 500, color: colors.ink}}>{form.name.trim() || t("CREATE_SUMMARY_UNNAMED")}</Typography>
                                <Typography variant="body2" sx={{color: colors.muted}}>{t("CREATE_SUMMARY_COUNTRY")}</Typography>
                                <Box sx={{mt: 1.5, p: 1.25, borderRadius: "4px", backgroundColor: colors.tableHeader, display: "flex", gap: 1.25, alignItems: "flex-start"}}>
                                    <Box aria-hidden sx={{width: 14, height: 14, mt: 0.25, borderRadius: "50%", backgroundColor: "#64B5F6", flexShrink: 0}} />
                                    <Box>
                                        <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: colors.ink}}>
                                            {t("CREATE_SUMMARY_ADMINISTRATOR", {alias: form.name.trim() || t("CREATE_SUMMARY_UNNAMED")})}
                                        </Typography>
                                        <Typography sx={{fontSize: "11.5px", color: colors.muted}}>
                                            {workingHours
                                                    ? t("CREATE_SUMMARY_CAN_LOG_IN_HOURS", {
                                                        days: `${t(`WEEKDAY_SHORT_${workingHours.firstDay.toUpperCase()}`)}-${t(`WEEKDAY_SHORT_${workingHours.lastDay.toUpperCase()}`)}`,
                                                        hours: `${workingHours.start}-${workingHours.end}`,
                                                    })
                                                    : t("CREATE_SUMMARY_CAN_LOG_IN")}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{px: 2, py: 1.5, borderBottom: `1px solid ${colors.sectionDivider}`}}>
                                <Box sx={{display: "flex", alignItems: "baseline", gap: 1}}>
                                    <Typography sx={{fontWeight: 500, color: selectedPlan ? colors.success : colors.faint}}>
                                        {selectedPlan?.name ?? t("CREATE_SUMMARY_NO_PLAN")}
                                    </Typography>
                                    <Typography variant="body2" sx={{color: colors.muted, flex: 1}}>
                                        {t("CREATE_SUMMARY_EMPLOYEES", {count: form.maxEmployees})}
                                        {form.termMonths ? ` · ${t("CREATE_TERM_MONTHS", {count: form.termMonths})}` : ""}
                                    </Typography>
                                    <Typography sx={{fontWeight: 500, color: colors.ink}}>
                                        {form.isFreePlan ? t("DETAIL_FREE_PLAN") : formatEuroCents(Math.round(form.amountDue * 100))}
                                    </Typography>
                                </Box>
                                <SubscriptionTimeline start={form.startDate} paidUntil={form.paidUntil} activeUntil={form.activeUntil} />
                            </Box>
                            <Box component="dl" sx={{px: 2, py: 1.5, m: 0, display: "grid", gridTemplateColumns: "1fr auto", rowGap: 0.75, fontSize: "12.5px"}}>
                                <Box component="dt" sx={{color: colors.muted}}>{t("CREATE_SUMMARY_FLAG_OVERRIDES")}</Box>
                                <Box component="dd" sx={{m: 0, textAlign: "right"}}>{t("CREATE_SUMMARY_FLAG_COUNTS", flagSummary)}</Box>
                                <Box component="dt" sx={{color: colors.muted}}>{t("DETAIL_QUOTAS")}</Box>
                                <Box component="dd" sx={{m: 0, textAlign: "right"}}>
                                    {t("CREATE_SUMMARY_QUOTAS", {
                                        sms: formatCount(form.quotas.sms),
                                        storage: formatStorageBytes(form.quotas.storageMegabytes * BYTES_PER_MEGABYTE),
                                        email: formatCount(form.quotas.email),
                                    })}
                                </Box>
                                <Box component="dt" sx={{color: colors.muted}}>{t("FEATURE_SMS_PROVIDER")}</Box>
                                <Box component="dd" sx={{m: 0, textAlign: "right"}}>{form.featureConfig.smsProvider === "IBASIS" ? "IBASIS" : "AWS SNS"}</Box>
                            </Box>
                        </Paper>

                        {submitAttempted && errorCount > 0 && (
                                <Box role="alert" sx={{display: "flex", gap: 1, alignItems: "center", p: 1.5, borderRadius: "4px", border: `1px solid ${colors.errorPanelBorder}`, backgroundColor: colors.errorPanelBackground, color: colors.errorPanelText, fontSize: "13px"}}>
                                    <ErrorOutlineIcon sx={{fontSize: 18}} />
                                    {t("CREATE_FIELDS_NEED_FIXING", {count: errorCount})}
                                </Box>
                        )}

                        <Box sx={{display: "flex", gap: 1}}>
                            <Button type="submit" variant="contained" size="large" disabled={createStatus === "loading"} sx={{flex: 1}}>
                                {createStatus === "loading" ? t("CREATE_BUTTON_CREATING") : t("CREATE_BUTTON_CREATE_TENANT")}
                                <KeyHint onDark>⌘↵</KeyHint>
                            </Button>
                            <Button component={Link} to="/tenants" variant="outlined" size="large">
                                {t("CANCEL")}
                                <KeyHint>Esc</KeyHint>
                            </Button>
                        </Box>
                        <Typography variant="body2" sx={{color: colors.muted}}>{t("CREATE_AFTER_SAVE_HINT")}</Typography>
                    </Box>
                </Box>

                <UnsavedChangesGuard
                        when={dirty && leaveTo === null}
                        title={t("CREATE_DISCARD_TITLE")}
                        body={t("CREATE_DISCARD_BODY")}
                        keepLabel={t("DETAIL_KEEP_EDITING")}
                        discardLabel={t("DETAIL_DISCARD_CONFIRM")}
                />
            </>
    );
}

function EmployeeStepper({value, onChange, error}: {value: number; onChange: (value: number) => void; error?: string}) {
    const {t} = useTranslation();
    return (
            <TextField
                    label={t("DETAIL_MAX_EMPLOYEES")}
                    type="number"
                    size="small"
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    error={Boolean(error)}
                    helperText={error}
                    sx={{width: 150}}
                    slotProps={{
                        htmlInput: {min: 1, style: {textAlign: "center"}},
                        input: {
                            startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton size="small" edge="start" aria-label={t("CREATE_FEWER_EMPLOYEES")} disabled={value <= 1} onClick={() => onChange(Math.max(1, value - 1))}>
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                            ),
                            endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" edge="end" aria-label={t("CREATE_MORE_EMPLOYEES")} onClick={() => onChange(value + 1)}>
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                            ),
                        },
                    }}
            />
    );
}

function SubscriptionTimeline({start, paidUntil, activeUntil}: {start: string; paidUntil: string; activeUntil: string}) {
    const {t} = useTranslation();
    const startTime = Date.parse(start);
    const paidTime = Date.parse(paidUntil);
    const activeTime = Date.parse(activeUntil);
    const valid = [startTime, paidTime, activeTime].every(Number.isFinite) && activeTime > startTime && paidTime >= startTime;
    const paidShare = valid ? Math.min(1, (paidTime - startTime) / (activeTime - startTime)) : 1;
    return (
            <Box sx={{mt: 1.5}} aria-hidden={!valid}>
                <Box sx={{display: "flex", height: 6, borderRadius: 3, overflow: "hidden", backgroundColor: colors.tableHeader}}>
                    <Box sx={{width: `${paidShare * 100}%`, backgroundColor: colors.primary}} />
                    <Box
                            sx={{
                                flex: 1,
                                backgroundImage: `repeating-linear-gradient(135deg, ${colors.expiring} 0 3px, transparent 3px 6px)`,
                                opacity: 0.7,
                            }}
                    />
                </Box>
                <Box sx={{display: "flex", justifyContent: "space-between", mt: 0.75, fontSize: "11px", color: colors.muted}}>
                    <span>{formatDate(start)}<br />{t("CREATE_TIMELINE_START")}</span>
                    <Box component="span" sx={{textAlign: "center"}}>{formatDate(paidUntil)}<br />{t("CREATE_TIMELINE_PAID_UNTIL")}</Box>
                    <Box component="span" sx={{textAlign: "right"}}>{formatDate(activeUntil)}<br />{t("CREATE_TIMELINE_ACCESS_ENDS")}</Box>
                </Box>
            </Box>
    );
}
